import { createContext, type ProviderProps, useContext, useEffect, useState } from 'react'
import { useResourceDefinitions, useStoreContext } from 'react-admin'

import type { Account } from '../types/account'

type ApiClientOptions = Omit<RequestInit, 'headers'> & { headers?: Iterable<readonly [string, string]> }
type ApiClient = (url: string, options?: ApiClientOptions) => Promise<{ json: unknown }>

type MeResponse = { accounts?: Account[] }

export type AccountsProviderApi = ReturnType<typeof createAccountsProvider>

const AccountsContext = createContext<AccountsProviderApi | null>(null)

export const AccountsProvider = (props: ProviderProps<AccountsProviderApi>) => <AccountsContext.Provider {...props} />

const useAccountsProvider = (): AccountsProviderApi => {
  const accountsProvider = useContext(AccountsContext)
  if (!accountsProvider) {
    throw new Error('useAccounts must be used inside an AccountsProvider')
  }
  return accountsProvider
}

export const useAccounts = () => {
  const accountsProvider = useAccountsProvider()
  const [currentAccount, setCurrentAccount] = useState<Account | null | undefined>(null)
  const store = useStoreContext()
  const resourcesDefinitions = useResourceDefinitions()
  const resources = Object.keys(resourcesDefinitions)

  useEffect(() => {
    ;(async () => setCurrentAccount(await accountsProvider.getCurrentAccount()))()
  }, [accountsProvider])
  return {
    accounts: accountsProvider.getAccounts(),
    currentAccount,
    setCurrentAccount: (account: Account) => {
      accountsProvider.setCurrentAccount(account)
      accountsProvider.navigateToCurrentAccount()
      store.removeItem('timeline')
      resources.forEach((resource) => {
        const key = `${resource}.listParams`
        store.removeItem(key)
      })
    },
  }
}

const byName = (a: Account, b: Account) => (a.name > b.name ? 1 : b.name > a.name ? -1 : 0)

const createAccountsProvider = (baseUrl: string, apiClient: ApiClient) => {
  const currentAccountIdKey = 'currentAccountId'
  let currentAccountId: string | null = null
  let cache: Account[] | null | undefined = null

  const clear = () => {
    window.localStorage.removeItem(currentAccountIdKey)
    currentAccountId = null
    cache = null
  }

  const fetchAccounts = async () => {
    try {
      const { json } = await apiClient(`${baseUrl}/me`)
      cache = (json as MeResponse).accounts?.sort(byName)
      return cache
    } catch {
      return null
    }
  }

  const getAccounts = (): Account[] => cache ?? []

  const setCurrentAccount = (account: Pick<Account, 'id'>) => {
    const { id } = account
    currentAccountId = id
    window.localStorage.setItem(currentAccountIdKey, id)
  }

  const getCurrentAccount = async (): Promise<Account | undefined> => {
    const accounts = cache ?? (await fetchAccounts())
    if (!currentAccountId) {
      currentAccountId = window.localStorage.getItem(currentAccountIdKey)
    }
    const currentAccount = accounts?.find((account) => account.id === currentAccountId)
    if (currentAccount) {
      return currentAccount
    }
    return accounts?.[0]
  }

  const navigateToCurrentAccount = () =>
    getCurrentAccount().then((currentAccount) => {
      if (currentAccount) {
        window.location.assign(`/${currentAccount.slug}`)
      }
    })

  const findAccountBySlug = (slug: string) => getAccounts().find((account) => account.slug === slug)

  const client: ApiClient = async (url, options) => {
    const currentAccount = await getCurrentAccount()
    return apiClient(url, {
      ...options,
      headers: new Headers({
        ...Object.fromEntries(options?.headers ?? []),
        'x-account-id': currentAccount?.id ?? 'none',
      }),
    })
  }

  return {
    clear,
    getAccounts,
    setCurrentAccount,
    getCurrentAccount,
    findAccountBySlug,
    navigateToCurrentAccount,
    client,
  }
}

export default createAccountsProvider
